import { assertUnreachable } from "@arti/utilities";
import { z } from "zod";

const egressProtocolSchema = z.enum(["http", "https"]);
export const TRANSPARENT_CHANNEL_ENVIRONMENT_EGRESS_PORT = 443;

export const channelEnvironmentEgressDenyPolicySchema = z.object({
  mode: z.literal("deny"),
});

export const channelEnvironmentEgressAllowlistRuleSchema = z.object({
  host: z.string().trim().min(1).max(253).toLowerCase(),
  includeSubdomains: z.boolean().default(false),
  ports: z
    .array(
      z
        .number()
        .int()
        .refine((port: number): boolean => port === 80 || port === 443, {
          message: "Egress allowlist ports must be 80 or 443.",
        }),
    )
    .min(1),
});

export const channelEnvironmentEgressAllowlistPolicySchema = z.object({
  mode: z.literal("allowlist"),
  protocols: z.array(egressProtocolSchema).min(1).default(["http", "https"]),
  rules: z.array(channelEnvironmentEgressAllowlistRuleSchema).default([]),
});

export const channelEnvironmentEgressPolicySchema = z.discriminatedUnion(
  "mode",
  [
    channelEnvironmentEgressDenyPolicySchema,
    channelEnvironmentEgressAllowlistPolicySchema,
  ],
);

export const channelEnvironmentTransparentEgressPolicySchema =
  channelEnvironmentEgressPolicySchema.superRefine((policy, context) => {
    if (policy.mode === "deny") {
      return;
    }
    policy.rules.forEach((rule, ruleIndex) => {
      rule.ports.forEach((port, portIndex) => {
        if (port !== TRANSPARENT_CHANNEL_ENVIRONMENT_EGRESS_PORT) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Transparent egress only supports HTTPS port 443.",
            path: ["rules", ruleIndex, "ports", portIndex],
          });
        }
      });
    });
  });

export type ChannelEnvironmentEgressPolicy = z.infer<
  typeof channelEnvironmentEgressPolicySchema
>;

export const DEFAULT_CHANNEL_ENVIRONMENT_EGRESS_POLICY = {
  mode: "deny",
} satisfies ChannelEnvironmentEgressPolicy;

export function serializeChannelEnvironmentEgressPolicy(
  policy: ChannelEnvironmentEgressPolicy,
): string {
  return JSON.stringify(policy);
}

export function parseChannelEnvironmentEgressPolicy(
  raw: string | null | undefined,
): ChannelEnvironmentEgressPolicy {
  if (!raw || raw === "deny") {
    return DEFAULT_CHANNEL_ENVIRONMENT_EGRESS_POLICY;
  }
  const parsed = JSON.parse(raw) as unknown;
  return channelEnvironmentEgressPolicySchema.parse(parsed);
}

export function parseChannelEnvironmentTransparentEgressPolicy(
  raw: string | null | undefined,
): ChannelEnvironmentEgressPolicy {
  const policy = parseChannelEnvironmentEgressPolicy(raw);
  return channelEnvironmentTransparentEgressPolicySchema.parse(policy);
}

export function getChannelEnvironmentEgressMode(
  policy: ChannelEnvironmentEgressPolicy,
): "allowlist" | "deny" {
  switch (policy.mode) {
    case "allowlist": {
      return "allowlist";
    }
    case "deny": {
      return "deny";
    }
    default: {
      throw assertUnreachable(policy);
    }
  }
}
